import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/discord-callback")({
  component: DiscordCallbackPage,
});

// Global flag to prevent duplicate processing
let isProcessingGlobal = false;

function DiscordCallbackPage() {
  const navigate = useNavigate();
  const { queryClient } = Route.useRouteContext();

  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Use both ref and global flag to prevent duplicate processing
    if (!hasProcessedRef.current && !isProcessingGlobal) {
      hasProcessedRef.current = true;
      isProcessingGlobal = true;
      handleDiscordCallback();
    }
  }, []);

  const handleDiscordCallback = async () => {
    try {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const error = urlParams.get("error");


      if (error) {
        throw new Error(`Discord OAuth error: ${error}`);
      }

      if (!code) {
        throw new Error("No authorization code received");
      }

      // Check if this code has already been processed
      const processedKey = `discord_oauth_code_${code}`;
      if (sessionStorage.getItem(processedKey)) {
        console.log("Code already processed, skipping...");
        throw new Error(
          "This authorization code has already been processed. Please try connecting again.",
        );
      }

      // Mark this code as processed
      sessionStorage.setItem(processedKey, "true");

      // Get project ID from localStorage (set during OAuth initiation)
      const projectId = localStorage.getItem("currentProjectId");
      if (!projectId) {
        throw new Error("No project ID found. Please try connecting again.");
      }

      // Exchange code for token
      // Use the same redirect URI that was used for authorization
      const redirectUri = `${window.location.origin}/discord-callback`;
      console.log("Token exchange redirect URI:", redirectUri);

      // Get user session for JWT token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - please log in first");
      }

      const { data, error: tokenError } = await supabase.functions.invoke(
        "discord-oauth",
        {
          body: {
            action: "token",
            code: code,
            state: state,
            redirectUri: redirectUri,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (tokenError) throw tokenError;
      if (!data.success) {
        throw new Error(data.error || "Failed to complete OAuth process");
      }

      // Clear stored project ID
      localStorage.removeItem("currentProjectId");

      // Check if we replaced an existing mapping
      if (data.replacedExistingMapping) {
        // Store warning message to show after redirect
        sessionStorage.setItem(
          "discordConnectionWarning",
          JSON.stringify({
            message: `이전에 ${data.replacedUserEmail || "다른 계정"}에 연동되어 있던 Discord 연동이 해제되고, 현재 계정으로 새로 연동되었습니다.`,
            timestamp: Date.now(),
          }),
        );
        toast.warning("Discord integration replaced", { duration: 2000 });
      } else {
        // Show success message
        toast.success("Discord connected");
      }

      queryClient.invalidateQueries({ queryKey: ["discordService"] });

      // Redirect to login page, which will auto-redirect to project
      navigate({ to: "/login", replace: true });
    } catch (err) {
      console.error("Discord callback error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to complete Discord OAuth";
      toast.error(errorMessage);

      // Still try to redirect after a delay
      setTimeout(() => {
        // Redirect to login page, which will auto-redirect to project
        navigate({ to: "/login", replace: true });
      }, 3000);
    } finally {
      // Reset global flag when done
      isProcessingGlobal = false;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">
          Processing authentication...
        </p>
      </div>
    </div>
  );
}